'use client'

import { useRef, useState, useCallback, useEffect } from 'react'

interface UseCarouselScrollOptions {
  itemCount: number
  autoplayDelay?: number
  /**
   * Whether `scrollToIndex` wraps past either end. Off by default: a carousel with arrows disabled
   * at the edges must not let the arrow keys jump from the first slide to the last. Turn it on for
   * looping carousels that navigate by dots, where there is no disabled state to contradict.
   * Autoplay cycles regardless.
   */
  loop?: boolean
}

/**
 * Shared scroll/autoplay/keyboard logic for snap-scroll carousels.
 * Used by CardCarousel, LearnFeaturedCarousel, and other carousel components.
 */
export function useCarouselScroll({
  itemCount,
  autoplayDelay = 10000,
  loop = false,
}: UseCarouselScrollOptions) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)
  const activeIndexRef = useRef(0)

  const updateActiveIndex = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const cards = el.querySelectorAll<HTMLElement>('[data-card]')
    if (cards.length === 0) return

    // Pitch = distance between adjacent cards' left edges. For gap-less
    // carousels this equals cardWidth; with a flex gap it equals
    // cardWidth + gap. Using the delta between two offsetLeft values
    // cancels out any offsetParent offset, so it's correct regardless of
    // the surrounding layout.
    const firstCard = cards[0]
    const pitch =
      cards.length > 1 ? cards[1].offsetLeft - firstCard.offsetLeft : firstCard.clientWidth
    if (pitch <= 0) return

    const index = Math.min(Math.round(el.scrollLeft / pitch), itemCount - 1)
    if (index !== activeIndexRef.current) {
      activeIndexRef.current = index
      setActiveIndex(index)
    }

    // Derive edge state from actual scroll position so multi-up carousels
    // (e.g. 3-up / 4-up) report "end" when the container can't scroll further
    // right, not only when the last item is centred.
    const maxScroll = el.scrollWidth - el.clientWidth
    setIsAtStart(el.scrollLeft <= 1)
    setIsAtEnd(maxScroll <= 0 || el.scrollLeft >= maxScroll - 1)
  }, [itemCount])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    el.addEventListener('scroll', updateActiveIndex, { passive: true })
    // Initial measurement (handles first render + breakpoint changes that
    // alter how many items fit at once).
    updateActiveIndex()

    // Coalesce resize events to one run per paint frame — window.resize can
    // fire dozens of times per second during a drag, and each run queries
    // the DOM plus up to three setState calls.
    let rafId = 0
    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updateActiveIndex)
    }
    window.addEventListener('resize', onResize)

    return () => {
      el.removeEventListener('scroll', updateActiveIndex)
      window.removeEventListener('resize', onResize)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [updateActiveIndex])

  useEffect(() => {
    if (!autoplayDelay || autoplayDelay <= 0 || itemCount <= 1) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return

    const timer = setInterval(() => {
      const next = (activeIndexRef.current + 1) % itemCount
      const el = scrollRef.current
      if (!el) return

      const card = el.querySelectorAll('[data-card]')[next] as HTMLElement
      if (!card) return

      el.scrollTo({ left: card.offsetLeft, behavior: 'smooth' })
    }, autoplayDelay)

    return () => clearInterval(timer)
  }, [autoplayDelay, itemCount])

  const scrollToIndex = useCallback(
    (index: number) => {
      const el = scrollRef.current
      if (!el) return

      const target = loop
        ? ((index % itemCount) + itemCount) % itemCount
        : Math.min(Math.max(index, 0), itemCount - 1)
      const card = el.querySelectorAll('[data-card]')[target] as HTMLElement
      if (!card) return

      el.scrollTo({ left: card.offsetLeft, behavior: 'smooth' })
    },
    [itemCount, loop],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        scrollToIndex(activeIndexRef.current - 1)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        scrollToIndex(activeIndexRef.current + 1)
      }
    },
    [scrollToIndex],
  )

  return { scrollRef, activeIndex, isAtStart, isAtEnd, scrollToIndex, handleKeyDown }
}
